import { Badge } from '@/components/ui/Badge/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type {
  CheckpointField,
  Field,
  GroupField,
  NumericField,
  PatchField,
  TaskField,
  ValueField,
} from '@certhon/models';
import { Link, Unlink } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FIELD_TYPE_OPTIONS } from './field-factory';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getFieldTypeLabel(type: Field['type']): string {
  const found = FIELD_TYPE_OPTIONS.find((o) => o.type === type);
  return found?.label ?? type;
}

type ConcreteValueField = CheckpointField | NumericField | TaskField;

function isValueField(field: Field): field is ConcreteValueField {
  return (
    field.type === 'CHECKPOINT_FIELD' ||
    field.type === 'NUMERIC_FIELD' ||
    field.type === 'TASK_FIELD'
  );
}

/** Whether a specific property is inherited (present in protoChain but NOT
 *  in modifiedFields). */
function isInherited(field: Field, propertyName: string): boolean {
  if (field.protoChain.length === 0) return false;
  return !field.modifiedFields.includes(propertyName);
}

// ---------------------------------------------------------------------------
// Debounced text field
// ---------------------------------------------------------------------------

interface DebouncedFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  /** Field has a protoChain — show inheritance button always. */
  hasProto?: boolean;
  /** Property is currently inherited (server-side, no local override yet). */
  inherited?: boolean;
  /** User has clicked unlock locally. */
  unlocked?: boolean;
  onUnlock?: () => void;
}

/** Text input that patches on blur or after a short debounce. */
function DebouncedField({
  label,
  value,
  onChange,
  multiline = false,
  hasProto,
  inherited,
  unlocked,
  onUnlock,
}: DebouncedFieldProps) {
  const [localValue, setLocalValue] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Sync from external (e.g. when navigating to a different field)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const flush = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (localValue !== value) {
      onChange(localValue);
    }
  }, [localValue, value, onChange]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = e.target.value;
      setLocalValue(next);
      // Debounce 600ms
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        if (next !== value) {
          onChange(next);
        }
      }, 600);
    },
    [onChange, value],
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const isInheritedField = !!inherited;
  const isLocked = isInheritedField && !unlocked;

  return (
    <div className="space-y-1.5">
      <Label className="text-muted-foreground text-xs">{label}</Label>
      <InputGroup
        hasProto={!!hasProto}
        locked={isLocked}
        canToggle={isInheritedField}
        onToggleLock={onUnlock}
      >
        {multiline ? (
          <Textarea
            value={localValue}
            onChange={handleChange}
            onBlur={flush}
            disabled={isLocked}
            className="disabled:bg-muted/40 rounded-none border-0 shadow-none focus-visible:ring-0
              disabled:opacity-100"
          />
        ) : (
          <Input
            value={localValue}
            onChange={handleChange}
            onBlur={flush}
            disabled={isLocked}
            className="disabled:bg-muted/40 rounded-none border-0 shadow-none focus-visible:ring-0
              disabled:opacity-100"
          />
        )}
      </InputGroup>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Input group: wraps a control with optional link-status / unlink addons
// ---------------------------------------------------------------------------

function InputGroup({
  hasProto,
  locked,
  canToggle,
  onToggleLock,
  children,
}: {
  /** Field has a protoChain (any inheritance applies). Drives button visibility. */
  hasProto: boolean;
  /** Effective lock state: inherited AND not user-unlocked. */
  locked: boolean;
  /** Whether toggling is currently allowed (only while inherited, no local override yet). */
  canToggle: boolean;
  onToggleLock?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'border-input flex items-stretch overflow-hidden rounded-md border',
        'focus-within:ring-ring focus-within:ring-1',
      )}
    >
      {hasProto && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={canToggle ? onToggleLock : undefined}
              disabled={!canToggle}
              className={cn(
                'border-input group flex shrink-0 items-center border-r px-2 transition-colors',
                canToggle && 'hover:bg-muted',
                !canToggle && 'cursor-not-allowed opacity-50',
              )}
              aria-pressed={!locked}
            >
              {/* Current state icon — hides on hover to preview the result */}
              {locked ? (
                <>
                  <Link className="h-3.5 w-3.5 group-hover:hidden" />
                  <Unlink className="hidden h-3.5 w-3.5 group-hover:block" />
                </>
              ) : (
                <>
                  <Unlink className="h-3.5 w-3.5 group-hover:hidden" />
                  <Link className="hidden h-3.5 w-3.5 group-hover:block" />
                </>
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            {!canToggle ? (
              <>
                This value has been overridden locally and is no longer
                inherited from the parent template.
              </>
            ) : locked ? (
              <>
                This value is inherited from the parent template. Editing it
                breaks the link — local changes will no longer track template
                updates.
              </>
            ) : (
              <>
                Unlinked from template. Click to relink and revert to the
                template value (only available before saving the override).
              </>
            )}
          </TooltipContent>
        </Tooltip>
      )}
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main form
// ---------------------------------------------------------------------------

interface FieldPropertiesFormProps {
  field: Field;
  onPatch: (partial: PatchField) => void;
}

export function FieldPropertiesForm({
  field,
  onPatch,
}: FieldPropertiesFormProps) {
  const hasProto = field.protoChain.length > 0;

  // Per-visit unlock state. Resets when navigating to a different field.
  // Inherited fields stay disabled until user explicitly clicks unlock.
  const [unlockedProps, setUnlockedProps] = useState<Set<string>>(
    () => new Set(),
  );
  useEffect(() => {
    setUnlockedProps(new Set());
  }, [field.id]);
  const toggleLock = useCallback((propertyName: string) => {
    setUnlockedProps((prev) => {
      const next = new Set(prev);
      if (next.has(propertyName)) next.delete(propertyName);
      else next.add(propertyName);
      return next;
    });
  }, []);

  if (field.type === 'ROOT_FIELD') {
    return null;
  }

  // Value-field types are interchangeable. Groups stay groups (different
  // semantics — children vs value).
  const valueFieldOptions = FIELD_TYPE_OPTIONS.filter(
    (o) => o.type !== 'GROUP_FIELD',
  );
  const canChangeType = isValueField(field);
  const typeInherited = hasProto && isInherited(field, 'type');

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const handleTypeChange = useCallback(
    (newType: string) => {
      if (newType === field.type) return;
      // Don't touch value — when value-type doesn't match new field-type,
      // helpers (calculateProgress, controls) treat it as unanswered.
      // Switching back to the original type restores the answer.
      onPatch({
        type: newType as 'CHECKPOINT_FIELD' | 'NUMERIC_FIELD' | 'TASK_FIELD',
      });
    },
    [field.type, onPatch],
  );

  return (
    <div className="space-y-4">
      {/* Field type */}
      <div className="space-y-1.5">
        <Label className="text-muted-foreground text-xs">Type</Label>
        {canChangeType ? (
          <InputGroup
            hasProto={hasProto}
            locked={typeInherited && !unlockedProps.has('type')}
            canToggle={typeInherited}
            onToggleLock={() => toggleLock('type')}
          >
            <Select
              value={field.type}
              onValueChange={handleTypeChange}
              disabled={typeInherited && !unlockedProps.has('type')}
            >
              <SelectTrigger
                className="disabled:bg-muted/40 flex-1 rounded-none border-0 shadow-none
                  focus-visible:ring-0 disabled:opacity-100"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {valueFieldOptions.map((o) => (
                  <SelectItem key={o.type} value={o.type}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </InputGroup>
        ) : (
          <Badge variant="outline" className="text-xs">
            {getFieldTypeLabel(field.type)}
          </Badge>
        )}
      </div>

      {/* Title NL */}
      <DebouncedField
        label="Title (NL)"
        value={(field as ValueField | GroupField).title_nl ?? ''}
        onChange={(v) => onPatch({ title_nl: v })}
        hasProto={hasProto}
        inherited={hasProto && isInherited(field, 'title_nl')}
        unlocked={unlockedProps.has('title_nl')}
        onUnlock={() => toggleLock('title_nl')}
      />

      {/* Title EN */}
      <DebouncedField
        label="Title (EN)"
        value={(field as ValueField | GroupField).title_en ?? ''}
        onChange={(v) => onPatch({ title_en: v })}
        hasProto={hasProto}
        inherited={hasProto && isInherited(field, 'title_en')}
        unlocked={unlockedProps.has('title_en')}
        onUnlock={() => toggleLock('title_en')}
      />

      {/* Description fields — only for value fields, not groups */}
      {isValueField(field) && (
        <>
          <DebouncedField
            label="Description (NL)"
            value={field.description_nl}
            onChange={(v) => onPatch({ description_nl: v })}
            multiline
            hasProto={hasProto}
            inherited={hasProto && isInherited(field, 'description_nl')}
            unlocked={unlockedProps.has('description_nl')}
            onUnlock={() => toggleLock('description_nl')}
          />
          <DebouncedField
            label="Description (EN)"
            value={field.description_en}
            onChange={(v) => onPatch({ description_en: v })}
            multiline
            hasProto={hasProto}
            inherited={hasProto && isInherited(field, 'description_en')}
            unlocked={unlockedProps.has('description_en')}
            onUnlock={() => toggleLock('description_en')}
          />
        </>
      )}

      {/* Proto chain info */}
      {hasProto && (
        <p className="text-muted-foreground mt-2 text-[11px]">
          <Link className="mr-1 inline-block h-3 w-3 align-text-top" />
          Linked properties are inherited from the parent template. Click the
          unlink icon to override.
        </p>
      )}
    </div>
  );
}
